/**
 * Cliente HTTP del editor de misiones — Fase 8d + edit mode.
 *
 *  - `GET    /api/campaigns`       lista para poblar el select.
 *  - `POST   /api/missions`        crear (JSON).
 *  - `GET    /api/missions/:id`    cargar para editar.
 *  - `PATCH  /api/missions/:id`    actualizar (JSON).
 *  - `DELETE /api/missions/:id`    borrar.
 */

import type { CampaignSummary, Mission } from '@51esc/shared';

export type FetchCampaignsResult =
  | { ok: true; data: CampaignSummary[] }
  | { ok: false; message: string };

export async function fetchCampaigns(): Promise<FetchCampaignsResult> {
  let res: Response;
  try {
    res = await fetch('/api/campaigns', { credentials: 'include' });
  } catch (err) {
    console.error('[mision-editor] fetchCampaigns network err', err);
    return { ok: false, message: 'No se pudo cargar la lista de campañas.' };
  }
  if (!res.ok) {
    return {
      ok: false,
      message: `El servidor respondió con ${String(res.status)}.`,
    };
  }
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  if (
    typeof body !== 'object' ||
    body === null ||
    !Array.isArray((body as { campaigns?: unknown }).campaigns)
  ) {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  return {
    ok: true,
    data: (body as { campaigns: CampaignSummary[] }).campaigns,
  };
}

export type CreateMissionResult =
  | { ok: true; mission: Mission }
  | { ok: false; message: string };

export async function createMission(
  payload: Record<string, unknown>,
): Promise<CreateMissionResult> {
  return submitJson('/api/missions', 'POST', payload);
}

export async function updateMission(
  id: string,
  payload: Record<string, unknown>,
): Promise<CreateMissionResult> {
  return submitJson(
    `/api/missions/${encodeURIComponent(id)}`,
    'PATCH',
    payload,
  );
}

async function submitJson(
  url: string,
  method: 'POST' | 'PATCH',
  payload: Record<string, unknown>,
): Promise<CreateMissionResult> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    console.error('[mision-editor] submitJson network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* lo manejamos abajo */
  }
  if (!res.ok) {
    const msg =
      typeof body === 'object' &&
      body !== null &&
      typeof (body as { error?: unknown }).error === 'string'
        ? ((body as { error: string }).error)
        : `El servidor respondió con ${String(res.status)}.`;
    return { ok: false, message: msg };
  }
  if (
    typeof body !== 'object' ||
    body === null ||
    typeof (body as { mission?: unknown }).mission !== 'object' ||
    (body as { mission?: unknown }).mission === null
  ) {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  return { ok: true, mission: (body as { mission: Mission }).mission };
}

export type FetchMissionResult =
  | { ok: true; mission: Mission }
  | { ok: false; message: string };

export async function fetchMission(id: string): Promise<FetchMissionResult> {
  let res: Response;
  try {
    res = await fetch(`/api/missions/${encodeURIComponent(id)}`, {
      credentials: 'include',
    });
  } catch (err) {
    console.error('[mision-editor] fetchMission network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok) {
    return {
      ok: false,
      message: `El servidor respondió con ${String(res.status)}.`,
    };
  }
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  if (
    typeof body !== 'object' ||
    body === null ||
    typeof (body as { mission?: unknown }).mission !== 'object' ||
    (body as { mission?: unknown }).mission === null
  ) {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  return { ok: true, mission: (body as { mission: Mission }).mission };
}

/**
 * Sube la imagen opcional de una misión (multipart). Solo el autor o
 * admin+. Sobrescribe la imagen previa si la había.
 */
export async function uploadMissionImage(
  id: string,
  file: File,
): Promise<{ ok: true } | { ok: false; message: string }> {
  const fd = new FormData();
  fd.append('image', file);
  let res: Response;
  try {
    res = await fetch(
      `/api/missions/${encodeURIComponent(id)}/image`,
      { method: 'POST', credentials: 'include', body: fd },
    );
  } catch (err) {
    console.error('[mision-editor] uploadMissionImage network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok) {
    let msg = `El servidor respondió con ${String(res.status)}.`;
    try {
      const body = (await res.json()) as { error?: string };
      if (typeof body.error === 'string') msg = body.error;
    } catch {
      /* ignore */
    }
    return { ok: false, message: msg };
  }
  return { ok: true };
}

/** Borra la imagen de una misión. Idempotente. */
export async function deleteMissionImage(
  id: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  let res: Response;
  try {
    res = await fetch(`/api/missions/${encodeURIComponent(id)}/image`, {
      method: 'DELETE',
      credentials: 'include',
    });
  } catch (err) {
    console.error('[mision-editor] deleteMissionImage network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok) {
    let msg = `El servidor respondió con ${String(res.status)}.`;
    try {
      const body = (await res.json()) as { error?: string };
      if (typeof body.error === 'string') msg = body.error;
    } catch {
      /* ignore */
    }
    return { ok: false, message: msg };
  }
  return { ok: true };
}

export async function deleteMission(
  id: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  let res: Response;
  try {
    res = await fetch(`/api/missions/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
  } catch (err) {
    console.error('[mision-editor] deleteMission network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok) {
    let msg = `El servidor respondió con ${String(res.status)}.`;
    try {
      const body = (await res.json()) as { error?: string };
      if (typeof body.error === 'string') msg = body.error;
    } catch {
      /* ignore */
    }
    return { ok: false, message: msg };
  }
  return { ok: true };
}
